// Money & currency helpers — extracted verbatim from partners-portal.jsx.
// Pure functions + currency constants. No React, no side effects.

export const CURRENCIES = [
  { code: 'USD', symbol: '$',  label: 'USD' },
  { code: 'EUR', symbol: '€',  label: 'EUR' },
  { code: 'GBP', symbol: '£',  label: 'GBP' },
  { code: 'ILS', symbol: '₪',  label: 'ILS' },
];

export const FALLBACK_RATES = { USD: 1, EUR: 0.92, GBP: 0.79, ILS: 3.7 };

// Convert amount from one currency to another (USD as base in rates object)
export const convertAmount = (amount, fromCurrency, toCurrency, rates) => {
  if (!amount || fromCurrency === toCurrency) return parseFloat(amount) || 0;
  const r = rates || FALLBACK_RATES;
  const inUSD = (parseFloat(amount) || 0) / (r[fromCurrency] || 1);
  return inUSD * (r[toCurrency] || 1);
};

// Format a monetary value in the given display currency, converting from the deal's native currency
export const fmtCurrency = (amount, displayCurrency, dealCurrency, rates) => {
  const from = dealCurrency || 'USD';
  const to   = displayCurrency || from;
  const converted = convertAmount(amount, from, to, rates);
  const sym = CURRENCIES.find(c => c.code === to)?.symbol || to;
  if (converted === 0 && !amount) return `${sym}—`;
  return `${sym}${Math.round(converted).toLocaleString()}`;
};

// Pure deal-math: derives totals from raw inputs (deductions + strategicPartners).
// Stored computed fields are unreliable (0/stale for Drafts and pre-storage rows),
// so always recompute from source data.
// Pool rule (May 2026): partners with `treatAsFixed === false` are members of an
// equal-share pool. After Basic-style (treatAsFixed=true) partners take their
// percentage cut from the pool, what's left is divided in equal slices among
// EvE plus each Pool member. Missing `treatAsFixed` falls back to true (legacy
// behaviour — preserves numbers on deals saved before this change).
export const computeDealTotals = (d) => {
  const totalReceived = parseFloat(d?.totalReceived) || parseFloat(d?.totalAgreed) || 0;
  const totalDeductions = (d?.deductions || []).reduce((sum, ded) => {
    if (ded.type === 'percentage') return sum + totalReceived * (parseFloat(ded.value) || 0) / 100;
    return sum + (parseFloat(ded.value) || 0);
  }, 0);
  const pool0 = totalReceived - totalDeductions;

  const sps = d?.strategicPartners || [];
  const isPoolMember = (sp) => sp.treatAsFixed === false;

  // Step 2: fixed-style partners eat first.
  const sumFixed = sps
    .filter(sp => !isPoolMember(sp))
    .reduce((sum, sp) => sum + pool0 * (parseFloat(sp.percentage) || 0) / 100, 0);

  // Step 3: Pool Fund = what's left. Floored at 0 so EvE/Pool never go negative
  // when fixed-style partners sum to >100% (the UI warns separately).
  const poolFund = Math.max(0, pool0 - sumFixed);
  const poolPartnerCount = sps.filter(isPoolMember).length;
  const poolSliceAmount = poolPartnerCount > 0 ? poolFund / (poolPartnerCount + 1) : 0;

  const partnerPayouts = sps.map(p => ({
    ...p,
    name: p.isExternal ? (p.externalName || '') : (p.name || ''),
    amount: isPoolMember(p)
      ? poolSliceAmount
      : pool0 * (parseFloat(p.percentage) || 0) / 100,
  }));

  const totalPartnerPayout = partnerPayouts.reduce((s, p) => s + p.amount, 0);
  // EvE gets one Pool slice if any pool members exist; otherwise the residual
  // (preserves legacy fixed-only deal behaviour exactly).
  const companyKeeps = poolPartnerCount > 0
    ? Math.round(poolSliceAmount * 100) / 100
    : Math.round((pool0 - totalPartnerPayout) * 100) / 100;
  const poolAmount = pool0; // backwards-compat alias for downstream callers

  return { totalReceived, totalDeductions, poolAmount, partnerPayouts, totalPartnerPayout, companyKeeps };
};

// Returns percentage formatted to 1 decimal, returns "0.0" when denominator is zero.
export const safePct = (num, den) => den === 0 ? '0.0' : ((num / den) * 100).toFixed(1);
